import { memo, useCallback, useContext, useState } from "react";
import PropTypes from "prop-types";
import Modal from "react-modal";
import Typography, { TYPOGRAPHY_VARIANTS } from "app/pages/.shared/Typography/Typography";
import AppGlobalsContext from "app/AppGlobalsContext";
import { Button } from "app/components/ui/Button/Button";
import Map from "app/pages/.shared/Map/Map";
import { RESOLUTION } from "app/pages/.shared/responsive/responsiveReducer";
import { Drawer } from "app/components/ui/drawer/Drawer";
import IconClose from "app/pages/.shared/IconClose";
import useBlockScroll from "app/utils/hooks/useBlockScroll";
import { useAnalytics } from "app/utils/analytics/useAnalytics";

import "./LocationModal.scss";
import { ArrowRightIcon } from "app/icons/navigation";

const LocationModal = ({ lat, lon, labelCta, title }) => {
	const { track } = useAnalytics();
	const [modalOpened, setModalOpen] = useState(false);
	const { resolution } = useContext(AppGlobalsContext);
	const isMobile = resolution === RESOLUTION.SMALL;
	useBlockScroll(modalOpened);

	const handleOpenModal = useCallback(() => {
		setModalOpen(true);
		track("product-view-on-map-click", {
			productName: title,
		});
	}, []);

	const handleCloseModal = useCallback(() => {
		setModalOpen(false);
	}, []);

	const map = (
		<Map
			latitude={lat}
			longitude={lon}
			zoom={11}
			fullscreenControl={false}
			mapTypeControl={false}
			autoOpen={true}
		/>
	);

	return (
		<>
			{labelCta && (
				<Button
					variant="borderless"
					endIcon={<ArrowRightIcon width={16} height={16} />}
					onClick={handleOpenModal}
					loading={false}
					className="location-modal__cta"
					data-testid="location-modal-cta"
				>
					{labelCta}
				</Button>
			)}
			{isMobile ? (
				<Drawer.Root open={modalOpened} onOpenChange={handleCloseModal}>
					<Drawer.Content>
						<Drawer.Header>
							<Typography
								variant={TYPOGRAPHY_VARIANTS.XL}
								isBold
								className="location-side-panel__header-title"
							>
								{title}
							</Typography>
						</Drawer.Header>

						<Drawer.Body>{map}</Drawer.Body>
					</Drawer.Content>
				</Drawer.Root>
			) : (
				<Modal
					isOpen={modalOpened}
					onRequestClose={handleCloseModal}
					className="location-modal"
					overlayClassName="location-modal__overlay"
				>
					<div className="location-modal__content" data-testid="location-modal">
						<div className="location-modal__header">
							<Typography
								variant={TYPOGRAPHY_VARIANTS.XL}
								isBold
								className="location-side-panel__header-title"
							>
								{title}
							</Typography>
							<IconClose
								className="location-modal__close"
								data-testid="location-modal-close"
								onClick={handleCloseModal}
								width={24}
								height={24}
							/>
						</div>
						{map}
					</div>
				</Modal>
			)}
		</>
	);
};

LocationModal.propTypes = {
	lat: PropTypes.number,
	lon: PropTypes.number,
	labelCta: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
	title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]),
};

export default memo(LocationModal);
